import type { OrderResponse } from 'apiConnector/secondary-market/shared/order';
import type { Action } from 'state/actions';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import type { OrderMap, OrderState } from 'state/secondaryMarket/shared/order/order.model';
import { addOrReplaceKey, patchKey } from 'state/utils/collection';

export function orderReducerFactory<PlaceOrderRes extends OrderResponse, Map extends OrderMap<PlaceOrderRes>>(
  actionType: Extract<OrderActionType, 'LIMIT_ORDER'>,
  initialOrderMap: () => Map,
  customReducer?: (state: Map, action: Action) => OrderMap<PlaceOrderRes>,
) {
  return (state: Map = initialOrderMap(), action: Action) => {
    switch (action.type) {
      case `SECONDARY_MARKET_${actionType}_PLACEMENT_REQUESTED` as const:
        return addOrReplaceKey(state, action.tabId, {
          orderStatus: 'ORDER_REQUESTED',
          tradeDate: undefined,
        } as OrderState<PlaceOrderRes>);
      case `SECONDARY_MARKET_${actionType}_PLACEMENT_REJECTED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              orderStatus: 'ORDER_REJECTED',
              errorMessage: action.error,
              timeout: action.timeout,
              tradeDate: undefined,
            }) as Partial<Map[string]>,
        );
      case `SECONDARY_MARKET_${actionType}_PLACEMENT_FAILED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              orderStatus: 'ORDER_FAILED',
              errorMessage: action.error,
              timeout: action.timeout,
            }) as Partial<Map[string]>,
        );
      case `SECONDARY_MARKET_${actionType}_PLACEMENT_RECEIVED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              ...action.data,
              orderStatus: 'ORDER_PLACED',
              errorMessage: undefined,
              timeout: undefined,
            }) as Partial<Map[string]>,
        );

      default:
        if (customReducer) {
          return customReducer(state, action);
        }
        return state;
    }
  };
}
